import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function PageHero() {
  return (
    <section className="relative min-h-[60vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]" />
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/8ff825bd6811236838cbf16bc457be81.jpg"
          alt="Pricing and Support"
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/80 via-transparent to-transparent" />
      </div>
      <div className="absolute top-20 right-20 w-80 h-80 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-64 h-64 bg-brand-purple-light/10 rounded-full blur-2xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-24 pt-40">
        <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Pricing &amp; Support
        </span>
        <h1 className="font-heading text-white text-6xl lg:text-7xl font-black mb-5 leading-tight max-w-3xl">
          Let&apos;s Talk About Your Journey
        </h1>
        <p className="font-body text-white/75 text-xl max-w-2xl leading-relaxed">
          Pricing is personalized — give us a call and we&apos;ll find the right fit for you, your schedule, and your goals.
        </p>
      </div>
    </section>
  );
}

function CallToGetPricingSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const handleCall = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  return (
    <section ref={ref} className="py-28 bg-gradient-to-br from-[#F0F4FF] via-white to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-16`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4">
            Personalized Pricing
          </span>
          <h2 className="font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark mb-5 leading-tight">
            Pricing Is Built <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">Around You</span>
          </h2>
          <p className="font-body text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Every student is different — so every plan is different. We don&apos;t do one-size-fits-all. Give us a call and we&apos;ll walk you through everything based on your goals, schedule, and situation.
          </p>
        </div>

        {/* 3 program tiers — no prices, just call */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-14">
          {[
            {
              icon: 'ri-seedling-line',
              name: 'Foundation',
              sessions: '1 Session / Week',
              desc: 'Perfect for beginners or those exploring creativity at a relaxed, steady pace.',
              features: ['1 personalized session weekly', 'All equipment provided', 'Progress check-ins', 'Customized lesson plan', 'Parent & guardian updates'],
              accent: 'from-[#EEF2FF] to-white',
              border: 'border-[#C7D2FE]',
              featured: false,
            },
            {
              icon: 'ri-fire-line',
              name: 'Standard',
              sessions: '2 Sessions / Week',
              desc: 'Ideal for consistent growth and building real momentum through regular practice.',
              features: ['2 personalized sessions weekly', 'All equipment provided', 'Bi-weekly progress reports', 'Advanced curriculum access', 'Priority scheduling'],
              accent: 'from-[#1E3A8A] to-[#5B21B6]',
              border: 'border-transparent',
              featured: true,
              badge: 'Most Popular',
            },
            {
              icon: 'ri-rocket-2-line',
              name: 'Elite Development',
              sessions: '3 Sessions / Week',
              desc: 'For those serious about accelerated growth and a deeper creative commitment.',
              features: ['3 personalized sessions weekly', 'All equipment provided', 'Weekly progress reports', 'Full creative curriculum', 'Performance & showcase prep'],
              accent: 'from-[#FEF9C3] to-white',
              border: 'border-brand-yellow/40',
              featured: false,
              badge: 'Advanced',
            },
          ].map((plan, i) => (
            <div
              key={plan.name}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} relative rounded-3xl overflow-hidden border-2 ${plan.border} card-lift flex flex-col`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {plan.badge && (
                <div className={`absolute top-5 right-5 px-3 py-1 rounded-full text-xs font-body font-semibold ${plan.featured ? 'bg-brand-yellow text-brand-blue-dark' : 'bg-brand-yellow/20 text-brand-yellow-dark'}`}>
                  {plan.badge}
                </div>
              )}
              <div className={`bg-gradient-to-br ${plan.accent} p-8`}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl mb-4 ${plan.featured ? 'bg-white/20' : 'bg-white border border-gray-100'}`}>
                  <i className={`${plan.icon} text-xl ${plan.featured ? 'text-white' : 'text-brand-blue-dark'}`} />
                </div>
                <h3 className={`font-heading text-xl font-black mb-1 ${plan.featured ? 'text-white' : 'text-brand-blue-dark'}`}>{plan.name}</h3>
                <p className={`font-body text-xs mb-4 ${plan.featured ? 'text-white/70' : 'text-gray-400'}`}>{plan.sessions}</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${plan.featured ? 'bg-brand-yellow text-brand-blue-dark' : 'bg-brand-blue/10 text-brand-blue-dark'} font-body font-bold text-sm`}>
                  <i className="ri-phone-line text-sm" /> Call for Pricing
                </div>
              </div>
              <div className="bg-white flex-1 p-8 flex flex-col">
                <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">{plan.desc}</p>
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 font-body text-sm text-gray-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                        <i className="ri-check-line text-xs" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleCall}
                  className={`whitespace-nowrap w-full py-3.5 rounded-xl font-body font-bold text-sm text-center cursor-pointer transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white hover:opacity-90'
                      : 'border-2 border-brand-purple text-brand-purple hover:bg-brand-purple hover:text-white'
                  }`}
                >
                  <i className="ri-phone-line mr-2" />Get My Pricing
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} max-w-4xl mx-auto p-8 rounded-2xl bg-gradient-to-r from-[#1E3A8A]/6 to-[#5B21B6]/6 border border-brand-purple/15 text-center`}>
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark mx-auto mb-4">
            <i className="ri-award-line text-xl" />
          </div>
          <p className="font-body text-gray-700 text-base leading-relaxed">
            <strong className="text-brand-blue-dark font-semibold">All plans receive the same level of quality, attention, and instruction.</strong> The difference is simply the frequency and consistency of development.
          </p>
          <p className="font-body text-gray-500 text-sm mt-3 leading-relaxed">
            Every student receives personalized instruction, dedicated mentorship, and the full commitment of the Creative Institute team.
          </p>
        </div>
      </div>
    </section>
  );
}

function CallCTABanner() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  const handleCall = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  return (
    <section ref={ref} className="py-16 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} flex flex-col lg:flex-row items-center justify-between gap-8 rounded-3xl border border-brand-yellow/30 bg-white/8 backdrop-blur-sm p-10`}>
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow flex-shrink-0">
              <i className="ri-phone-fill text-brand-blue-dark text-3xl" />
            </div>
            <div>
              <h3 className="font-heading text-white text-2xl lg:text-3xl font-black mb-1">Ready to Get Started?</h3>
              <p className="font-body text-white/70 text-base">Give us a call — we&apos;ll talk through your goals and find the right plan for you.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 flex-shrink-0">
            <button
              onClick={handleCall}
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base cursor-pointer hover:bg-brand-yellow-light transition-all"
            >
              <i className="ri-phone-line text-lg" /> Schedule a Free Call
            </button>
            <a
              href="tel:9102424567"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/40 text-white font-body font-semibold text-base cursor-pointer hover:border-brand-yellow hover:text-brand-yellow transition-all"
            >
              <i className="ri-phone-fill text-lg" /> 910-242-4567
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function PaymentOptionsSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-20 bg-white border-y border-gray-100">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-12`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-blue/8 text-brand-blue-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-blue/15 mb-4">
            Flexible Payments
          </span>
          <h2 className="font-heading text-4xl font-black text-brand-blue-dark mb-3">
            Flexible Payment Options
          </h2>
          <p className="font-body text-gray-500 text-base max-w-xl mx-auto">
            We want to make it easy to get started. Creative Institute offers multiple ways to pay so that finances never stand in the way of your creative development.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            {
              icon: 'ri-bank-card-line',
              title: 'Monthly Membership',
              desc: 'Pay month to month with no long-term contract. Simple, straightforward, and flexible.',
              highlight: false,
            },
            {
              icon: 'ri-calendar-check-line',
              title: 'Afterpay Available',
              desc: 'Split your payments into installments with Afterpay. Get started today and pay over time with zero extra stress.',
              highlight: true,
            },
            {
              icon: 'ri-discuss-line',
              title: 'Payment Plans',
              desc: 'Need a custom arrangement? We are happy to work with you directly to create a payment plan that fits your situation.',
              highlight: false,
            },
          ].map((opt) => (
            <div
              key={opt.title}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} p-7 rounded-2xl card-lift group ${opt.highlight ? 'bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white' : 'bg-[#F8FAFF] border border-[#E0E7FF] hover:border-brand-purple/30'}`}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ${opt.highlight ? 'bg-brand-yellow text-brand-blue-dark' : 'bg-brand-purple/10 text-brand-purple'}`}>
                <i className={`${opt.icon} text-xl`} />
              </div>
              <h3 className={`font-heading text-lg font-bold mb-2 ${opt.highlight ? 'text-white' : 'text-brand-blue-dark'}`}>{opt.title}</h3>
              <p className={`font-body text-sm leading-relaxed ${opt.highlight ? 'text-white/75' : 'text-gray-500'}`}>{opt.desc}</p>
            </div>
          ))}
        </div>
        <div className={`scroll-hidden delay-400 ${isVisible ? 'scroll-visible' : ''} text-center mt-8`}>
          <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-sm cursor-pointer hover:bg-brand-purple hover:text-white transition-colors">
            <i className="ri-phone-line" /> Ask About Payment Options
          </Link>
        </div>
      </div>
    </section>
  );
}

function AccessibilitySection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#F0F4FF] via-white to-[#F5F3FF] relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#EEF2FF] rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-[#FEF9C3]/60 rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-14`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs uppercase tracking-widest font-semibold border border-brand-yellow/30 mb-4">
            Accessibility
          </span>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4">
            Making It Accessible for Everyone
          </h2>
          <p className="font-body text-gray-500 text-base max-w-2xl mx-auto">
            We are committed to ensuring that financial barriers never stand in the way of a creative&apos;s growth.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className={`scroll-hidden slide-left ${isVisible ? 'scroll-visible' : ''} rounded-3xl overflow-hidden border-2 border-brand-yellow/30 card-lift`}>
            <div className="bg-gradient-to-br from-[#FEF9C3] to-[#FEF3C7] p-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-brand-yellow mb-5">
                <i className="ri-medal-line text-3xl text-brand-blue-dark" />
              </div>
              <h3 className="font-heading text-brand-blue-dark text-2xl font-black mb-2">Community Discounts</h3>
              <p className="font-body text-gray-700 text-sm">
                We proudly honor those who serve and protect our community.
              </p>
            </div>
            <div className="bg-white p-8">
              <p className="font-body text-gray-600 text-base leading-relaxed mb-6">
                Creative Institute offers special discounted rates for those who dedicate their lives to serving others:
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  { icon: 'ri-shield-star-line', label: 'Military Families', desc: 'Active duty, veterans, and their immediate families' },
                  { icon: 'ri-hospital-line', label: 'First Responders', desc: 'Police, fire, EMS, and emergency personnel' },
                  { icon: 'ri-community-line', label: 'Community Service Professionals', desc: 'Social workers, educators, and non-profit workers' },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <div>
                      <p className="font-body text-brand-blue-dark text-sm font-semibold">{item.label}</p>
                      <p className="font-body text-gray-400 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand-yellow text-brand-yellow-dark font-body font-semibold text-sm cursor-pointer hover:bg-brand-yellow hover:text-brand-blue-dark transition-colors">
                <i className="ri-phone-line" /> Inquire About Discounts
              </Link>
            </div>
          </div>

          <div className={`scroll-hidden slide-right ${isVisible ? 'scroll-visible' : ''} rounded-3xl overflow-hidden card-lift`}>
            <div className="bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] p-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/15 mb-5">
                <i className="ri-hand-heart-line text-3xl text-white" />
              </div>
              <h3 className="font-heading italic text-white text-2xl font-black mb-2">Financial Assistance</h3>
              <p className="font-body text-white/80 text-sm italic">
                No creative should be turned away due to financial limitations.
              </p>
            </div>
            <div className="bg-white p-8 border border-[#C7D2FE]">
              <p className="font-heading italic text-gray-600 text-base leading-relaxed mb-4">
                We understand that every situation is different. Limited financial assistance options are available for families in need.
              </p>
              <p className="font-body text-gray-500 text-sm leading-relaxed italic mb-8">
                Our goal is to ensure that no creative is ever turned away due to financial limitations. Every request is met with compassion, care, and a genuine desire to help. You are not alone — reach out and let us walk with you.
              </p>
              <div className="p-5 rounded-2xl bg-[#EEF2FF] mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue-dark flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-sm" />
                  </div>
                  <p className="font-body text-brand-blue-dark text-xs leading-relaxed">
                    Financial assistance is limited and subject to availability. Reach out directly to discuss your situation with our team.
                  </p>
                </div>
              </div>
              <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity">
                <i className="ri-arrow-right-circle-line" /> Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: quoteRef, isVisible: quoteVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: cardsRef, isVisible: cardsVis } = useScrollAnimation({ threshold: 0.1 });

  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="story"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d72p5piaegmdqoca82mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-28 bg-[#FFFBF2] relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-brand-purple/8 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>
            Real Families. Real Stories.
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading italic text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4 leading-tight`}>
            Words From the Families<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D97706] to-[#7C3AED]">We&apos;ve Had the Honor to Serve</span>
          </h2>
        </div>

        <div ref={quoteRef} className={`scroll-hidden ${quoteVis ? 'scroll-visible' : ''} max-w-4xl mx-auto mb-16`}>
          <div className="relative rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] p-10 lg:p-14 overflow-hidden">
            <div className="absolute -top-4 -left-2 font-heading text-[180px] leading-none text-white/5 select-none pointer-events-none" aria-hidden="true">&ldquo;</div>
            <div className="absolute -bottom-10 -right-2 font-heading text-[180px] leading-none text-white/5 select-none pointer-events-none" aria-hidden="true">&rdquo;</div>
            <div className="relative z-10">
              <p className="font-heading italic text-white text-xl lg:text-2xl leading-relaxed mb-6">
                &ldquo;I was a single mother trying to give my son every opportunity I could. When I found Creative Institute, I knew it was exactly where he needed to be. They worked with me without making me feel small. They just said, &lsquo;let&apos;s figure it out together.&rsquo; That kindness changed our lives.&rdquo;
              </p>
              <div>
                <p className="font-heading text-brand-yellow text-base font-bold">Tamara Williams</p>
                <p className="font-body text-white/60 text-xs mt-0.5 uppercase tracking-widest">Mother of Marcus, Age 12 &mdash; Enrolled 2026</p>
              </div>
            </div>
          </div>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {[
            {
              quote: "We never imagined our daughter would have access to a program like this. The team gave her a place where she finally felt understood and celebrated. She lights up every single time.",
              name: "Darius & Keisha Johnson",
              role: "Parents of Aaliyah, Age 9 — Enrolled 2026",
            },
            {
              quote: "As a family going through a really hard season, we didn't expect anyone to meet us where we were. Creative Institute did exactly that. My son has grown so much — not just in his art, but in who he is becoming.",
              name: "Monique Carter",
              role: "Mother of Isaiah, Age 14 — Enrolled 2026",
            },
          ].map((t, i) => (
            <div
              key={t.name}
              className={`scroll-hidden ${cardsVis ? 'scroll-visible' : ''} delay-${(i + 1) * 200} rounded-2xl bg-white border border-brand-yellow/20 p-8 card-lift flex flex-col gap-5`}
            >
              <i className="ri-double-quotes-l text-3xl text-brand-yellow/40" />
              <p className="font-heading italic text-gray-700 text-base leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="font-heading text-brand-blue-dark text-sm font-bold">{t.name}</p>
                  <p className="font-body text-gray-400 text-xs mt-0.5">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`scroll-hidden delay-400 ${cardsVis ? 'scroll-visible' : ''} text-center mt-12`}>
          <p className="font-heading italic text-gray-500 text-lg mb-5">Your story could be next. Don&apos;t let finances be the reason you wait.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#D97706] to-[#7C3AED] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity">
              <i className="ri-hand-heart-line text-lg" /> Reach Out for Assistance
            </a>
            <button
              onClick={() => { setModalOpen(true); setSubmitted(false); setCharCount(0); setConsentChecked(false); }}
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-[#D97706] text-[#D97706] font-body font-bold text-sm cursor-pointer hover:bg-[#D97706]/10 transition-colors bg-transparent"
            >
              <i className="ri-quill-pen-line text-lg" /> Share Your Story
            </button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setModalOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative z-10 w-full max-w-lg bg-[#FFFBF2] rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-gradient-to-r from-[#D97706] to-[#7C3AED] px-8 py-7">
              <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer">
                <i className="ri-close-line text-base" />
              </button>
              <p className="font-body text-white/70 text-xs uppercase tracking-widest mb-1">Your Voice Matters</p>
              <h3 className="font-heading italic text-white text-2xl font-bold leading-snug">Share Your Family&apos;s Story</h3>
              <p className="font-body text-white/80 text-sm mt-2 leading-relaxed">Your words could give another family the courage to reach out.</p>
            </div>
            <div className="px-8 py-7">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-yellow/20 mx-auto mb-4">
                    <i className="ri-heart-fill text-3xl text-[#D97706]" />
                  </div>
                  <h4 className="font-heading italic text-brand-blue-dark text-xl font-bold mb-2">Thank You So Much</h4>
                  <p className="font-body text-gray-500 text-sm leading-relaxed">Your story has been received. We are deeply grateful.</p>
                  <button onClick={() => setModalOpen(false)} className="mt-6 whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#D97706] to-[#7C3AED] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity">Close</button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Family Name *</label>
                      <input type="text" name="family_name" required placeholder="e.g. The Williams Family" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-[#D97706] transition-colors placeholder:text-gray-300" />
                    </div>
                    <div>
                      <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Child&apos;s Name &amp; Age *</label>
                      <input type="text" name="child_name_age" required placeholder="e.g. Marcus, Age 12" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-[#D97706] transition-colors placeholder:text-gray-300" />
                    </div>
                  </div>
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Program Enrolled In</label>
                    <input type="text" name="program" placeholder="e.g. In-Home Creative Program" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-[#D97706] transition-colors placeholder:text-gray-300" />
                  </div>
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Email Address</label>
                    <input type="email" name="email" placeholder="your@email.com" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-[#D97706] transition-colors placeholder:text-gray-300" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">Your Story *</label>
                      <span className={`font-body text-xs ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>{charCount}/500</span>
                    </div>
                    <textarea name="story" required maxLength={500} rows={4} placeholder="Tell us about your experience..." onChange={(e) => setCharCount(e.target.value.length)} className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-[#D97706] transition-colors resize-none placeholder:text-gray-300" />
                  </div>
                  <button type="submit" disabled={submitting || charCount > 500} className="whitespace-nowrap w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-[#D97706] to-[#7C3AED] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed mt-1">
                    {submitting ? <><i className="ri-loader-4-line animate-spin" /> Sending...</> : <><i className="ri-send-plane-fill" /> Submit My Story</>}
                  </button>
                  <label className="flex items-start gap-3 cursor-pointer group mt-1">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input type="checkbox" name="consent_public" value="yes" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} className="sr-only" />
                      <div onClick={() => setConsentChecked(!consentChecked)} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 cursor-pointer ${consentChecked ? 'bg-gradient-to-br from-[#D97706] to-[#7C3AED] border-transparent' : 'bg-white border-gray-300 group-hover:border-[#D97706]'}`}>
                        {consentChecked && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </div>
                    <span onClick={() => setConsentChecked(!consentChecked)} className="font-body text-gray-500 text-xs leading-relaxed group-hover:text-gray-700 transition-colors cursor-pointer">
                      <strong className="text-gray-700 font-semibold">I give Creative Institute permission</strong> to feature my family&apos;s story publicly.
                    </span>
                  </label>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function NightClassesBanner() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-10 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-r from-[#1E3A8A]/80 to-[#5B21B6]/80 backdrop-blur-sm p-8 lg:p-10`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 flex items-start gap-6">
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow/20 border border-brand-yellow/30">
                <i className="ri-moon-clear-line text-3xl text-brand-yellow" />
              </div>
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow font-body text-xs font-bold uppercase tracking-widest mb-3">Evening Sessions Available</span>
                <h2 className="font-heading text-white text-2xl lg:text-3xl font-black mb-2 leading-tight">Night Classes — Learn After Dark</h2>
                <p className="font-body text-white/70 text-base leading-relaxed max-w-xl">Busy during the day? Our evening sessions run until 9pm, Monday through Saturday. Same quality instruction, flexible timing for working professionals, students, and night owls.</p>
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-wrap justify-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 font-body text-xs font-semibold"><i className="ri-time-line text-brand-yellow" /> Until 9pm</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 font-body text-xs font-semibold"><i className="ri-calendar-line text-brand-yellow" /> Mon – Sat</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PricingSupportPage() {
  return (
    <main>
      <PageHero />
      <NightClassesBanner />
      <CallToGetPricingSection />
      <CallCTABanner />
      <PaymentOptionsSection />
      <AccessibilitySection />
      <TestimonialSection />
    </main>
  );
}
